import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

// Componentes compatíveis com shadcn/ui
const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const AlertShadcn = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
AlertShadcn.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  )
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

// Componente original (mantido para compatibilidade)
interface AlertProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  className?: string;
}

const alertConfig = {
  success: {
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
    borderClass: 'border-emerald-200',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-500',
    borderClass: 'border-red-200',
  },
  warning: {
    icon: AlertCircle,
    iconClass: 'text-yellow-500',
    borderClass: 'border-yellow-200',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    borderClass: 'border-blue-200',
  },
};

// Componente original (mantido para compatibilidade)
function AlertOriginal({ message, type, className = '' }: AlertProps) {
  const config = alertConfig[type];
  const IconComponent = config.icon;

  return (
    <div
      className={cn('border-gray-200 rounded-md border px-4 py-3', config.borderClass, className)}
    >
      <p className="text-sm">
        <IconComponent
          className={cn('me-3 -mt-0.5 inline-flex', config.iconClass)}
          size={16}
          aria-hidden="true"
        />
        {message}
      </p>
    </div>
  );
}

// Componentes específicos para facilitar o uso
export function SuccessAlert({ message, className }: { message: string; className?: string }) {
  return <AlertOriginal message={message} type="success" className={className} />;
}

export function ErrorAlert({ message, className }: { message: string; className?: string }) {
  return <AlertOriginal message={message} type="error" className={className} />;
}

export function WarningAlert({ message, className }: { message: string; className?: string }) {
  return <AlertOriginal message={message} type="warning" className={className} />;
}

export function InfoAlert({ message, className }: { message: string; className?: string }) {
  return <AlertOriginal message={message} type="info" className={className} />;
}

// Exportar componentes compatíveis com shadcn/ui como padrão
export { AlertShadcn as Alert, AlertTitle, AlertDescription };
